import type { FilteredProduct, RankDecoration } from '@/api';
import type { MedicationPadItemType, ReissueStatusType } from '@/components/PatientPageV2/components/PrescriptionPad';
import type { MedicationType } from '@/components/PatientPageV2/components/ProductDetailsCard/ProductDetailsCard.types';
import { transformFilteredProductToPrescriptionMetadata } from '@/components/PatientPageV2/components/ProductDetailsCard/ProductDetailsCard.utils';
import { DEFAULT_QUESTION_ID, REISSUED_ANSWER_ID } from '@/shared-ui/TierLabel/TierLabel.types';
import { RankTier } from '@montugroup/pms-api-contracts';
import type { PrescriptionHistoryStatePreviousItem } from '../types';
import { getErrorMessageForMedicationItem } from './getErrorMessageForMedicationItem';

/**
 * Creates a new reissued medication by merging it with existing prescribed medications,
 * considering out-of-stock or inactive scenarios.
 *
 * @param availableProducts - All available medications for the doctor to choose from.
 * @param reissuedMedicationItem - The reissued medication to be added to the pad.
 * @param validateDosageMinLength - Optional flag for dosage length validation, default is false.
 * @returns A new reissued medication item or undefined if productId is missing or invalid.
 */
export const createReissuedMedication = (
  availableProducts: FilteredProduct[],
  reissuedMedicationItem: MedicationPadItemType,
  previouslyPrescribedItem: PrescriptionHistoryStatePreviousItem | undefined,
  flags: {
    ffPatPrescriptionDosageMinLengthValidation: boolean;
    ffPatPrefillDosage: boolean;
  }
): MedicationPadItemType | undefined => {
  const { medicationPadItem } = reissuedMedicationItem;
  const productId = medicationPadItem?.productId;

  // Validate and parse the productId to a number
  const numericProductId = productId !== undefined && !isNaN(Number(productId)) ? Number(productId) : undefined;
  if (numericProductId === undefined) {
    return undefined;
  }

  const matchingProduct = availableProducts.find((product) => product.id === numericProductId);

  // Ensure medicationPadItem is defined before calling getUpdatedMedicationPadItem
  const updatedMedicationPadItem =
    matchingProduct && medicationPadItem
      ? getUpdatedMedicationPadItem(
          medicationPadItem,
          matchingProduct,
          previouslyPrescribedItem?.prescriptionDetails,
          flags.ffPatPrefillDosage
        )
      : medicationPadItem;

  const isOutsideTopRecommendation = matchingProduct ? !isTopRecommendation(matchingProduct) : false;

  const updatedReissueStatus = buildReissueStatus(
    reissuedMedicationItem.reissueStatus,
    medicationPadItem,
    matchingProduct
  );

  const errorMessage = getErrorMessageForMedicationItem(
    updatedMedicationPadItem,
    flags.ffPatPrescriptionDosageMinLengthValidation
  );

  // Build the new MedicationPadItemType object
  return {
    ...reissuedMedicationItem,
    medicationPadItem: updatedMedicationPadItem,
    moreProductsLoaded: false,
    feedback: isOutsideTopRecommendation
      ? {
          questionAsked: DEFAULT_QUESTION_ID,
          answerSelected: REISSUED_ANSWER_ID
        }
      : undefined,
    reissueStatus: updatedReissueStatus,
    errorMessage
  };
};

const getUpdatedMedicationPadItem = (
  medicationPadItem: MedicationType,
  matchingProduct: FilteredProduct,
  previousPrescriptionDetails?: PrescriptionHistoryStatePreviousItem['prescriptionDetails'],
  ffPatRefillDosage: boolean = false
): MedicationType => ({
  ...medicationPadItem,
  ...(previousPrescriptionDetails && ffPatRefillDosage
    ? {
        dosage: previousPrescriptionDetails.dosage,
        interval: previousPrescriptionDetails.interval,
        repeats: previousPrescriptionDetails.repeats,
        quantity: previousPrescriptionDetails.quantity,
        productName: matchingProduct.productName,
        supplierName: matchingProduct.supplierName,
        dispenseSize: matchingProduct.attributes?.dispenseSize
      }
    : transformFilteredProductToPrescriptionMetadata(matchingProduct))
});

const isTopRecommendation = (product: FilteredProduct): boolean => {
  const decoration = product.decoration as RankDecoration;
  return decoration.tier === RankTier.First;
};

const buildReissueStatus = (
  currentReissueStatus: ReissueStatusType | undefined,
  medicationPadItem: MedicationType | undefined,
  matchingProduct?: FilteredProduct
): ReissueStatusType => {
  const fallbackProductId =
    medicationPadItem?.productId !== undefined && !isNaN(Number(medicationPadItem.productId))
      ? Number(medicationPadItem.productId)
      : undefined;

  const productId = matchingProduct?.id ?? fallbackProductId;
  const productName = matchingProduct?.productName || medicationPadItem?.productName || '';
  const supplierName = matchingProduct?.supplierName || medicationPadItem?.supplierName || '';
  const description = matchingProduct?.description || medicationPadItem?.description || '';

  return {
    ...currentReissueStatus,
    productId,
    productName,
    supplierName,
    description
  };
};
